import { useSelector } from 'react-redux';
import { useRef } from 'react';
import { Container, Group, GridCol, Grid, Button } from '@mantine/core';
import { fetchEquipmentPeriodReport, PeriodTotals } from '../../../../libraries/periods';
import { Language } from '../../../languages/languageHandler';
import { ICurrency } from '../../../../libraries/currencies';
import { AppState } from '../../../client-redux/reducers';
import ModalComponent from '../../../components/modal';
import { formatDateTimeLabel } from '../../../utils/date';
import { formatCurrAmount, getLocationName } from '../../../utils/script';
import PeriodCurrencyTable from '../period-table/period-currency-table';
import DisplayField from '../../../components/display-field';
import { useStatusOperDialog } from '../../../context/status-oper-dialog';
import { IconFileTypeXls, IconFileTypePdf } from '@tabler/icons-react';
import './styles.scss';

interface ShowPeriodProps {
  period: PeriodTotals;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
  currency?: ICurrency;
  isDeposit?: boolean;
}

const ShowPeriodDetails = ({ period, openDetails, hideBtn, lang, isDeposit }: ShowPeriodProps) => {
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const controller = useRef(new AbortController()).current;
  const { handleApiError } = useStatusOperDialog();

  const getPeriodReport = async (format: string, eid: number, periodId: number) => {
    if (!format || !eid || !periodId) return;

    try {
      await fetchEquipmentPeriodReport(format, eid, periodId, controller);
    } catch (err: any) {
      handleApiError(err, lang.PERIODS_REPORT_ERROR || 'Error generating report');
    }
  };

  return (
    <ModalComponent
      show={openDetails}
      handleClick={hideBtn}
      title={`${lang.ENTERPRISE_PERIOD} ${period.eid}-${period.period}`}
      content={
        <>
          <Container classNames={{ root: 'period-details' }}>
            <Group>
              <Grid gap="sm">
                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.EQUIPMENT_LOCAL} content={getLocationName(period.sid)} />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.EQUIPMENT_ID} content={period.eid} />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.GLOBAL_PERIOD} content={period.period} />
                </GridCol>

                {period.code && (
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.ENTERPRISE_EQUIP_PERIODS_PAYMENT_CLOSE_CODE} content={period.code} />
                  </GridCol>
                )}

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.GLOBAL_DATE} content={formatDateTimeLabel(period.dateTimeOpen, region)} />
                </GridCol>

                {period.userOpen && (
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.ENTERPRISE_EQUIP_PERIODS_USER_OPEN}
                      content={`${period.userOpen?.uid} - ${period.userOpen?.name}`}
                    />
                  </GridCol>
                )}

                {period.dateTimeClose && (
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.ENTERPRISE_EQUIP_PERIODS_DT_CLOSE}
                      content={formatDateTimeLabel(period.dateTimeClose, region)}
                    />
                  </GridCol>
                )}

                {period.userClose && (
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField
                      title={lang.ENTERPRISE_EQUIP_PERIODS_USER_CLOSE}
                      content={`${period.userClose?.uid} - ${period.userClose?.name}`}
                    />
                  </GridCol>
                )}
              </Grid>

              {!isDeposit &&
                (period.totals ?? []).map((pa, index) => {
                  const curr = currencies.find(c => c.cid === pa.cid);
                  if (!curr || !pa.total) return null;

                  return (
                    <Group key={index}>
                      <Grid gap="sm">
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField title={lang.PERIOD_TOTAL_AMOUNT} content={formatCurrAmount(pa.total, curr)} />
                        </GridCol>
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField title={lang.PERIOD_TOTAL_PAID} content={formatCurrAmount(pa.paid, curr)} />
                        </GridCol>
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField
                            title={lang.PERIOD_TOTAL_PAID_CASH}
                            content={formatCurrAmount(pa.paid - pa.paidAP, curr)}
                          />
                        </GridCol>
                        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                          <DisplayField
                            title={lang.PERIOD_TOTAL_PAID_CARD}
                            content={formatCurrAmount(pa.paidAP, curr)}
                          />
                        </GridCol>
                      </Grid>

                      <PeriodCurrencyTable
                        key={index}
                        lang={lang}
                        currency={curr}
                        cash={period.mc ? period.mc.filter(m => m.cid === pa.cid) : []}
                      />
                    </Group>
                  );
                })}

              {isDeposit &&
                (period.totals ?? []).map((pa, index) => {
                  const curr = currencies.find(c => c.cid === pa.cid);
                  if (!curr || !pa.total) return null;

                  return (
                    <PeriodCurrencyTable
                      key={index}
                      lang={lang}
                      label={`${lang.GLOBAL_AMOUNT}: ${formatCurrAmount(pa.total, curr)}`}
                      currency={curr}
                      cash={period.mc ? period.mc.filter(m => m.cid === pa.cid) : []}
                    />
                  );
                })}

              <Group className="buttons">
                {period.useReport && (
                  <Button radius="md" onClick={() => getPeriodReport('pdf', period.eid, period.period)}>
                    <IconFileTypePdf stroke={2} />
                    PDF
                  </Button>
                )}

                {period.dateTimeClose && (
                  <Button radius="md" onClick={() => getPeriodReport('xls', period.eid, period.period)}>
                    <IconFileTypeXls stroke={2} />
                    XLS
                  </Button>
                )}
              </Group>
            </Group>
          </Container>
        </>
      }
    />
  );
};

export default ShowPeriodDetails;
